import { Link, useParams } from '@tanstack/react-router';
import { AdminRole } from '@qaytim/shared';
import { CoinsIcon, ImageIcon, ReceiptTextIcon, TicketIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DetailShell, DetailSkeleton } from '@/components/detail-shell';
import { UserActions } from '@/components/user-actions';
import { StatCard } from '@/components/stat-card';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice } from '@/components/states';
import { authStore } from '@/lib/auth-store';
import { useUserDetail } from '@/lib/admin-queries';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

/**
 * Foydalanuvchi kartochkasi — o'z manzilida.
 *
 * Ilgari bu ro'yxat ostidagi ochiladigan blok edi. Uch narsa uni ko'chirishga majbur qildi:
 * qo'llab-quvvatlash suhbatida kartochkaning manzili ulashiladi, ish yarmida uzilganda
 * "orqaga" ishlashi kerak, va ochilgan kartochka ro'yxatni ekrandan pastga surib yuborardi.
 *
 * Har bir tarix qatori endi O'Z detail sahifasiga havola: shu paytgacha "bu chek nima bo'ldi"
 * degan savol bilan operator moderatsiya ekraniga o'tib, u yerda id bo'yicha qidirishi kerak
 * edi.
 */
export function UserDetailPage() {
  const { userId } = useParams({ from: '/shell/users/$userId' });
  const detail = useUserDetail(userId);
  /*
    Amallar bloki faqat `admin` roliga. Operatorga tugmalarni ko'rsatib, keyin 403 bilan rad
    etish — ishlamaydigan tugma taklif qilish demakdir. Bu KO'RINISH qarori: haqiqiy chegara
    serverda (`@RequireAdminRole`), va u panelning nimani chizganidan mustaqil.
  */
  const isAdmin = authStore.get()?.admin.role === AdminRole.ADMIN;

  return (
    <DetailShell
      title={detail.data?.user.displayName ?? strings.users.cardHeading}
      description={strings.users.subtitle}
      backTo="/users"
    >
      {detail.isError ? <ErrorNotice error={detail.error} /> : null}
      {detail.isPending ? <DetailSkeleton /> : null}

      {detail.data ? (
        <>
          <UserActions user={detail.data.user} visible={isAdmin} />

          <Card>
            <CardHeader>
              <CardTitle>{strings.users.cardHeading}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard
                  label={strings.users.balanceColumn}
                  value={detail.data.user.currentBalance}
                  icon={CoinsIcon}
                  hint={`${strings.users.balanceLedger}: ${detail.data.ledgerBalance}`}
                />
                <StatCard
                  label={strings.users.statTickets}
                  value={detail.data.tickets.length}
                  icon={TicketIcon}
                />
                <StatCard
                  label={strings.users.statReceipts}
                  value={detail.data.receipts.length}
                  icon={ReceiptTextIcon}
                />
                <StatCard
                  label={strings.users.statPhotos}
                  value={detail.data.photos.length}
                  icon={ImageIcon}
                />
              </div>

              {/*
                Ikkala qiymat yonma-yon: farq bo'lsa, uni birinchi ko'radigan odam shu ekranni
                ochgan operator bo'ladi (tungi reconciliation faqat log yozadi).
              */}
              {detail.data.user.currentBalance !== detail.data.ledgerBalance ? (
                <ErrorNotice>{strings.users.balanceMismatch}</ErrorNotice>
              ) : (
                <p className="text-muted-foreground text-xs">{strings.users.balanceOk}</p>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <span className="text-muted-foreground tabular font-mono text-xs">
                  {detail.data.user.telegramId}
                </span>
                {/* Sovrinni topshirish shu raqamdan boshlanadi — kartochkaning ko'rinar joyida. */}
                {detail.data.user.phoneNumber ? (
                  <a
                    className="tabular font-mono text-xs hover:underline"
                    href={`tel:${detail.data.user.phoneNumber}`}
                  >
                    {detail.data.user.phoneNumber}
                  </a>
                ) : (
                  <span className="text-muted-foreground text-xs">
                    {strings.users.phoneMissing}
                  </span>
                )}
                {detail.data.user.isBlocked ? (
                  <Badge variant="destructive">{strings.users.blocked}</Badge>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.users.ticketsHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.data.tickets.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={TicketIcon} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{strings.reveal.winnerTicket}</TableHead>
                      <TableHead>{strings.draws.columnStatus}</TableHead>
                      <TableHead>{strings.moderation.columnAcquired}</TableHead>
                      <TableHead>{strings.moderation.columnMonth}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.data.tickets.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell className="font-mono font-medium">{ticket.code}</TableCell>
                        <TableCell>
                          <StatusBadge>{ticket.status}</StatusBadge>
                        </TableCell>
                        <TableCell className="tabular">{ticket.acquiredDate}</TableCell>
                        <TableCell className="tabular">{ticket.monthKey}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" asChild>
                            <Link
                              to="/moderation/tickets/$ticketId"
                              params={{ ticketId: ticket.id }}
                            >
                              {strings.common.open}
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.users.receiptsHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.data.receipts.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={ReceiptTextIcon} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{strings.moderation.columnScannedAt}</TableHead>
                      <TableHead>{strings.draws.columnStatus}</TableHead>
                      <TableHead className="text-right">{strings.moderation.columnTotal}</TableHead>
                      <TableHead className="text-right">
                        {strings.moderation.columnPoints}
                      </TableHead>
                      <TableHead className="text-right">{strings.moderation.columnItems}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.data.receipts.map((receipt) => (
                      <TableRow key={receipt.id}>
                        <TableCell className="tabular whitespace-nowrap">
                          {formatTashkent(receipt.scannedAt)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            <StatusBadge>{receipt.status}</StatusBadge>
                            {receipt.revoked ? (
                              <Badge variant="destructive">{strings.moderation.revoked}</Badge>
                            ) : null}
                          </span>
                        </TableCell>
                        <TableCell className="tabular text-right">{receipt.totalAmount}</TableCell>
                        <TableCell className="tabular text-right">
                          {receipt.pointsAwarded}
                        </TableCell>
                        <TableCell className="tabular text-right">{receipt.itemCount}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" asChild>
                            <Link
                              to="/moderation/receipts/$receiptId"
                              params={{ receiptId: receipt.id }}
                            >
                              {strings.common.open}
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.users.photosHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.data.photos.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={ImageIcon} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{strings.moderation.columnScannedAt}</TableHead>
                      <TableHead>{strings.draws.columnStatus}</TableHead>
                      <TableHead className="text-right">
                        {strings.moderation.columnRecognised}
                      </TableHead>
                      <TableHead className="text-right">{strings.moderation.columnItems}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.data.photos.map((photo) => (
                      <TableRow key={photo.id}>
                        <TableCell className="tabular whitespace-nowrap">
                          {formatTashkent(photo.createdAt)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge>{photo.status}</StatusBadge>
                        </TableCell>
                        <TableCell className="tabular text-right">
                          {photo.recognisedItemCount}
                        </TableCell>
                        <TableCell className="tabular text-right">{photo.itemCount}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" asChild>
                            <Link
                              to="/moderation/photos/$photoScanId"
                              params={{ photoScanId: photo.id }}
                            >
                              {strings.common.open}
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.users.ledgerHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.data.ledger.length === 0 ? (
                <EmptyState title={strings.app.empty} icon={CoinsIcon} />
              ) : (
                <LedgerTable rows={detail.data.ledger} />
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </DetailShell>
  );
}

export interface LedgerRow {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  createdAt: string;
}

/**
 * Ball harakati jadvali.
 *
 * Uchta detail sahifada ham bir xil ko'rinadi (foydalanuvchi, chek, rasm) — chunki savol bir
 * xil: "bu yerda ball qanday harakat qilgan". Manfiy miqdor ajratib ko'rsatiladi, chunki
 * qaytarilgan ball qo'llab-quvvatlash so'rovining eng ko'p uchraydigan sababi.
 */
export function LedgerTable({ rows }: { rows: LedgerRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{strings.moderation.columnScannedAt}</TableHead>
          <TableHead>{strings.dlq.columnType}</TableHead>
          <TableHead className="text-right">{strings.moderation.columnPoints}</TableHead>
          <TableHead className="text-right">{strings.users.balanceColumn}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="tabular whitespace-nowrap">
              {formatTashkent(row.createdAt)}
            </TableCell>
            <TableCell className="font-mono text-xs">{row.type}</TableCell>
            <TableCell className="tabular text-right">
              <span className={row.amount < 0 ? 'text-destructive font-medium' : ''}>
                {row.amount}
              </span>
            </TableCell>
            <TableCell className="tabular text-right">{row.balanceAfter}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
